import type { TFunction } from 'i18next';
import type {
  DecryptedFileSecretData,
  FileSecretData,
  FileSecretField,
  SecretFileFieldKey,
  SecretFileGroup,
  SecretFileGroupKey,
  SecretFilePresetType,
} from '../../interfaces/FileSecret';
import { isSecretFilePresetType } from '../../interfaces/FileSecret';

const generateId = () => crypto.randomUUID();

const CARD_BRANDS = ['visa', 'mastercard', 'amex', 'other'] as const;
const IDENTITY_TITLES = ['mr', 'ms', 'mx', 'dr', 'prof', 'other'] as const;

function fieldLabel(t: TFunction, key: SecretFileFieldKey): string {
  return t(`secret.fields.${key}`);
}

function groupLabel(t: TFunction, key: SecretFileGroupKey): string {
  return t(`secret.groups.${key}`);
}

function makeGroup(
  key: SecretFileGroupKey,
  order: number,
  t: TFunction,
  locked = true,
): SecretFileGroup {
  return {
    id: generateId(),
    key,
    label: groupLabel(t, key),
    order,
    locked,
  };
}

function makeField(
  partial: Omit<FileSecretField, 'id' | 'value'> & { value?: string },
): FileSecretField {
  return {
    id: generateId(),
    value: '',
    ...partial,
  };
}

/** Notes-only Additional options + empty Custom fields (non-custom presets). */
function makeTrailingGroups(
  order: number,
  t: TFunction,
): {
  additional: SecretFileGroup;
  customFields: SecretFileGroup;
  notes: FileSecretField;
} {
  const additional = makeGroup('additional_options', order, t);
  const customFields = makeGroup('custom_fields', order + 1, t);
  return {
    additional,
    customFields,
    notes: makeField({
      key: 'notes',
      type: 'text',
      label: fieldLabel(t, 'notes'),
      groupId: additional.id,
      order: 0,
      locked: true,
      ui: { multiline: true },
    }),
  };
}

function credentialsPreset(t: TFunction): DecryptedFileSecretData {
  const login = makeGroup('credentials_main', 0, t);
  const autofill = makeGroup('credentials_autofill', 1, t);
  const { additional, customFields, notes } = makeTrailingGroups(2, t);
  return {
    schemaVersion: 2,
    name: '',
    encrypted: false,
    preset: 'credentials',
    groups: [login, autofill, additional, customFields],
    fields: [
      makeField({
        key: 'username',
        type: 'username',
        label: fieldLabel(t, 'username'),
        groupId: login.id,
        order: 0,
        locked: true,
      }),
      makeField({
        key: 'password',
        type: 'password',
        label: fieldLabel(t, 'password'),
        groupId: login.id,
        order: 1,
        locked: true,
        ui: { generator: true, mask: true },
      }),
      makeField({
        key: 'website',
        type: 'url',
        label: fieldLabel(t, 'website'),
        groupId: autofill.id,
        order: 0,
        locked: false,
        ui: { repeatable: true },
      }),
      notes,
    ],
  };
}

function cardPreset(t: TFunction): DecryptedFileSecretData {
  const group = makeGroup('card_main', 0, t);
  const { additional, customFields, notes } = makeTrailingGroups(1, t);
  return {
    schemaVersion: 2,
    name: '',
    encrypted: false,
    preset: 'card',
    groups: [group, additional, customFields],
    fields: [
      makeField({
        key: 'cardholder',
        type: 'text',
        label: fieldLabel(t, 'cardholder'),
        groupId: group.id,
        order: 0,
        locked: true,
      }),
      makeField({
        key: 'number',
        type: 'password',
        label: fieldLabel(t, 'number'),
        groupId: group.id,
        order: 1,
        locked: true,
        ui: { mask: true },
      }),
      makeField({
        key: 'brand',
        type: 'text',
        label: fieldLabel(t, 'brand'),
        groupId: group.id,
        order: 2,
        locked: true,
        ui: { options: [...CARD_BRANDS] },
      }),
      makeField({
        key: 'exp_month',
        type: 'month',
        label: fieldLabel(t, 'exp_month'),
        groupId: group.id,
        order: 3,
        locked: true,
      }),
      makeField({
        key: 'exp_year',
        type: 'year',
        label: fieldLabel(t, 'exp_year'),
        groupId: group.id,
        order: 4,
        locked: true,
      }),
      makeField({
        key: 'cvv',
        type: 'password',
        label: fieldLabel(t, 'cvv'),
        groupId: group.id,
        order: 5,
        locked: true,
        ui: { mask: true },
      }),
      notes,
    ],
  };
}

function identityPreset(t: TFunction): DecryptedFileSecretData {
  const personal = makeGroup('identity_personal', 0, t);
  const ids = makeGroup('identity_ids', 1, t);
  const contact = makeGroup('identity_contact', 2, t);
  const address = makeGroup('identity_address', 3, t);
  const { additional, customFields, notes } = makeTrailingGroups(4, t);

  return {
    schemaVersion: 2,
    name: '',
    encrypted: false,
    preset: 'identity',
    groups: [personal, ids, contact, address, additional, customFields],
    fields: [
      makeField({
        key: 'title',
        type: 'text',
        label: fieldLabel(t, 'title'),
        groupId: personal.id,
        order: 0,
        locked: true,
        ui: { options: [...IDENTITY_TITLES] },
      }),
      makeField({
        key: 'first_name',
        type: 'text',
        label: fieldLabel(t, 'first_name'),
        groupId: personal.id,
        order: 1,
        locked: true,
      }),
      makeField({
        key: 'last_name',
        type: 'text',
        label: fieldLabel(t, 'last_name'),
        groupId: personal.id,
        order: 2,
        locked: true,
      }),
      makeField({
        key: 'fiscal_code',
        type: 'text',
        label: fieldLabel(t, 'fiscal_code'),
        groupId: ids.id,
        order: 0,
        locked: true,
      }),
      makeField({
        key: 'passport',
        type: 'text',
        label: fieldLabel(t, 'passport'),
        groupId: ids.id,
        order: 1,
        locked: true,
      }),
      makeField({
        key: 'driving_license',
        type: 'text',
        label: fieldLabel(t, 'driving_license'),
        groupId: ids.id,
        order: 2,
        locked: true,
      }),
      makeField({
        key: 'email',
        type: 'email',
        label: fieldLabel(t, 'email'),
        groupId: contact.id,
        order: 0,
        locked: true,
      }),
      makeField({
        key: 'phone',
        type: 'phone',
        label: fieldLabel(t, 'phone'),
        groupId: contact.id,
        order: 1,
        locked: true,
      }),
      makeField({
        key: 'address1',
        type: 'text',
        label: fieldLabel(t, 'address1'),
        groupId: address.id,
        order: 0,
        locked: true,
      }),
      makeField({
        key: 'address2',
        type: 'text',
        label: fieldLabel(t, 'address2'),
        groupId: address.id,
        order: 1,
        locked: true,
      }),
      makeField({
        key: 'address3',
        type: 'text',
        label: fieldLabel(t, 'address3'),
        groupId: address.id,
        order: 2,
        locked: true,
      }),
      makeField({
        key: 'city',
        type: 'text',
        label: fieldLabel(t, 'city'),
        groupId: address.id,
        order: 3,
        locked: true,
      }),
      makeField({
        key: 'state',
        type: 'text',
        label: fieldLabel(t, 'state'),
        groupId: address.id,
        order: 4,
        locked: true,
      }),
      makeField({
        key: 'zip',
        type: 'text',
        label: fieldLabel(t, 'zip'),
        groupId: address.id,
        order: 5,
        locked: true,
      }),
      makeField({
        key: 'country',
        type: 'text',
        label: fieldLabel(t, 'country'),
        groupId: address.id,
        order: 6,
        locked: true,
      }),
      notes,
    ],
  };
}

function sshKeyPreset(t: TFunction): DecryptedFileSecretData {
  const group = makeGroup('ssh_main', 0, t);
  const { additional, customFields, notes } = makeTrailingGroups(1, t);
  return {
    schemaVersion: 2,
    name: '',
    encrypted: false,
    preset: 'ssh_key',
    groups: [group, additional, customFields],
    fields: [
      makeField({
        key: 'private_key',
        type: 'password',
        label: fieldLabel(t, 'private_key'),
        groupId: group.id,
        order: 0,
        locked: true,
        ui: { mask: true, multiline: true, upload: true },
      }),
      makeField({
        key: 'public_key',
        type: 'text',
        label: fieldLabel(t, 'public_key'),
        groupId: group.id,
        order: 1,
        locked: true,
        ui: { multiline: true },
      }),
      makeField({
        key: 'fingerprint',
        type: 'text',
        label: fieldLabel(t, 'fingerprint'),
        groupId: group.id,
        order: 2,
        locked: true,
      }),
      notes,
    ],
  };
}

function customPreset(t: TFunction): DecryptedFileSecretData {
  // One ungrouped starter field; no default group (DnD still works once user adds groups).
  return {
    schemaVersion: 2,
    name: '',
    encrypted: false,
    preset: 'custom',
    groups: [],
    fields: [createCustomField(undefined, 0, t)],
  };
}

/**
 * Build a v2 DecryptedFileSecretData template for the given preset.
 * Labels resolved via i18n keys (secret.fields.* / secret.groups.*).
 */
export function createSecretFilePreset(
  preset: SecretFilePresetType,
  t: TFunction,
): DecryptedFileSecretData {
  switch (preset) {
    case 'credentials':
      return credentialsPreset(t);
    case 'card':
      return cardPreset(t);
    case 'identity':
      return identityPreset(t);
    case 'ssh_key':
      return sshKeyPreset(t);
    case 'custom':
    default:
      return customPreset(t);
  }
}

function sortFields(fields: FileSecretField[]): FileSecretField[] {
  return [...fields].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

function sortGroups(groups: SecretFileGroup[]): SecretFileGroup[] {
  return [...groups].sort((a, b) => a.order - b.order);
}

/** Root keys owned by schema; everything else is forward-compat pass-through. */
const KNOWN_SECRET_ROOT_KEYS = new Set([
  'schemaVersion',
  'name',
  'encrypted',
  'fields',
  'hint',
  'preset',
  'groups',
  'extensions',
  'updatedAt',
]);

function unknownSecretRootKeys(data: object): Record<string, unknown> {
  const extras: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data)) {
    if (!KNOWN_SECRET_ROOT_KEYS.has(key) && value !== undefined) {
      extras[key] = value;
    }
  }
  return extras;
}

/**
 * Normalize on-disk / decrypted payload to in-memory v2 shape.
 * Legacy (no preset/groups) → custom, empty groups, fields ungrouped.
 * Missing / unknown field.groupId → undefined (ungrouped).
 * Preserves extensions / updatedAt / field&group meta / unknown root keys.
 * Does not throw on unknown/extra properties.
 */
export function normalizeSecretFileData(
  data: Pick<
    FileSecretData,
    | 'name'
    | 'encrypted'
    | 'hint'
    | 'schemaVersion'
    | 'preset'
    | 'groups'
    | 'extensions'
    | 'updatedAt'
  > & {
    fields: FileSecretField[];
  } & Record<string, unknown>,
  t: TFunction,
): DecryptedFileSecretData {
  const schemaVersion = data.schemaVersion ?? 1;
  const rawPreset = data.preset ?? 'custom';
  const preset: SecretFilePresetType | string = rawPreset;

  let groups = data.groups
    ? sortGroups(
        data.groups.map((group, index) => ({
          ...group,
          order: typeof group.order === 'number' ? group.order : index,
        })),
      )
    : [];

  if (
    resolveSecretFilePreset(preset) !== 'custom' &&
    !groups.some((g) => g.key === 'custom_fields')
  ) {
    // Older preset files may lack Custom fields; append empty group for Add field.
    const maxOrder = groups.reduce((max, g) => Math.max(max, g.order), -1);
    groups = [
      ...groups,
      {
        id: generateId(),
        key: 'custom_fields',
        label: groupLabel(t, 'custom_fields'),
        order: maxOrder + 1,
        locked: true,
      },
    ];
  }

  const knownGroupIds = new Set(groups.map((g) => g.id));
  const fields = sortFields(
    data.fields.map((field, index) => ({
      ...field,
      groupId: field.groupId && knownGroupIds.has(field.groupId) ? field.groupId : undefined,
      order: typeof field.order === 'number' ? field.order : index,
    })),
  );

  // Unknown root keys kept so serialize can round-trip future schema.
  return {
    ...unknownSecretRootKeys(data),
    schemaVersion: Math.max(schemaVersion, 1),
    name: data.name,
    encrypted: data.encrypted,
    hint: data.hint,
    preset,
    groups,
    fields,
    ...(data.extensions ? { extensions: data.extensions } : {}),
    ...(data.updatedAt ? { updatedAt: data.updatedAt } : {}),
  } as DecryptedFileSecretData;
}

/** Resolve UI preset: unknown string → custom. */
export function resolveSecretFilePreset(
  preset: SecretFilePresetType | string | undefined,
): SecretFilePresetType {
  if (preset && isSecretFilePresetType(preset)) return preset;
  return 'custom';
}

/** Serialize decrypted data for disk (schemaVersion always 2). Passes through unknown root keys. */
export function serializeSecretFileData(
  data: DecryptedFileSecretData,
  fieldsPayload: FileSecretField[] | string,
): FileSecretData {
  return {
    ...unknownSecretRootKeys(data as object),
    schemaVersion: 2,
    name: data.name,
    encrypted: data.encrypted,
    fields: fieldsPayload,
    ...(data.hint ? { hint: data.hint } : {}),
    preset: data.preset,
    groups: data.groups,
    ...(data.extensions ? { extensions: data.extensions } : {}),
    ...(data.updatedAt ? { updatedAt: data.updatedAt } : {}),
  };
}

export function createWebsiteField(groupId: string, order: number, t: TFunction): FileSecretField {
  return makeField({
    key: 'website',
    type: 'url',
    label: fieldLabel(t, 'website'),
    groupId,
    order,
    locked: false,
    ui: { repeatable: true },
  });
}

export function createCustomField(
  groupId: string | undefined,
  order: number,
  t: TFunction,
): FileSecretField {
  return makeField({
    type: 'text',
    label: t('secret.fieldLabel'),
    ...(groupId ? { groupId } : {}),
    order,
    locked: false,
  });
}

export { CARD_BRANDS, IDENTITY_TITLES };
